/* ==========================================================================
   MERCH EXPRESS — HOMEPAGE BEHAVIOUR
   --------------------------------------------------------------------------
   One file for every mx-home-* section. Each module finds its own roots and
   no-ops when that section is not on the page, so sections stay independently
   removable in the Theme Editor while sharing this one script.

   Every module is idempotent: it marks each root it has wired and skips it on
   a second pass. That matters because Shopify re-runs section scripts on
   shopify:section:load while the merchant edits, and because the theme uses
   InstantClick, which can replay DOMContentLoaded handlers.
   ========================================================================== */
(function () {
  'use strict';

  /* Every section tag emits this script, so the browser parses it once per
     section on the page. One run is enough — bail out of the rest rather
     than stacking twelve copies of every global listener. */
  if (window.__mxHomeLoaded) return;
  window.__mxHomeLoaded = true;

  var WIRED = 'mxWired';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Marks a root as handled. Returns false if it was already wired. */
  function claim(el, key) {
    if (!el) return false;
    var flag = WIRED + key;
    if (el.dataset[flag]) return false;
    el.dataset[flag] = '1';
    return true;
  }

  function each(root, sel, fn) {
    Array.prototype.forEach.call((root || document).querySelectorAll(sel), fn);
  }

  /* ========================================================================
     ANNOUNCEMENT — rotates the claims on narrow screens only
     ====================================================================== */
  function initAnnouncement(scope) {
    each(scope, '[data-mx-ann]', function (root) {
      if (!claim(root, 'Ann')) return;

      var items = root.querySelectorAll('.mx-ann__item');
      if (items.length < 2) return;

      var narrow = window.matchMedia('(max-width: 760px)');
      var index = 0;
      var timer = null;

      function show(i) {
        items.forEach(function (el, n) {
          el.classList.toggle('mx-ann__item--on', n === i);
        });
      }

      function start() {
        stop();
        show(index);
        timer = setInterval(function () {
          index = (index + 1) % items.length;
          show(index);
        }, 4200);
      }

      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      function sync() {
        if (narrow.matches) {
          /* Tells CSS that JS owns visibility from here on. Until this class
             lands, the stylesheet shows the first claim on its own, so the
             bar is never blank when the script does not run. */
          root.classList.add('mx-ann--rotating');
          start();
        } else {
          stop();
          root.classList.remove('mx-ann--rotating');
          items.forEach(function (el) { el.classList.remove('mx-ann__item--on'); });
        }
      }

      sync();
      if (narrow.addEventListener) narrow.addEventListener('change', sync);
      else narrow.addListener(sync);
    });
  }

  /* ========================================================================
     HEADER — mobile drawer + predictive search
     ====================================================================== */
  function initHeader(scope) {
    each(scope, '[data-mx-head]', function (root) {
      if (!claim(root, 'Head')) return;

      /* ── Mobile drawer ─────────────────────────────────────────────── */
      var burger = root.querySelector('[data-mx-burger]');
      var drawer = root.querySelector('[data-mx-drawer]');

      if (burger && drawer) {
        burger.addEventListener('click', function () {
          var open = burger.getAttribute('aria-expanded') === 'true';
          burger.setAttribute('aria-expanded', open ? 'false' : 'true');
          burger.setAttribute('aria-label', open ? 'Open menu' : 'Close menu');
          drawer.hidden = open;
        });

        /* Escape closes it and puts focus back on the control that opened it */
        root.addEventListener('keydown', function (e) {
          if (e.key !== 'Escape' || drawer.hidden) return;
          burger.setAttribute('aria-expanded', 'false');
          burger.setAttribute('aria-label', 'Open menu');
          drawer.hidden = true;
          burger.focus();
        });
      }

      /* ── Dropdowns (snippets/mx-nav-mega.liquid) ─────────────────────── */
      /* CSS opens them on hover and focus. This adds what CSS cannot:
         lazy images on first open, the hover preview, aria-expanded,
         Escape to close, and tap-to-open on touch screens. */
      var menuItems = root.querySelectorAll('[data-mx-menu]');
      var coarse = window.matchMedia('(hover: none)');

      function loadImages(item) {
        if (item.dataset.mxLoaded) return;
        item.dataset.mxLoaded = '1';
        Array.prototype.forEach.call(item.querySelectorAll('img[data-mx-src]'), function (img) {
          img.src = img.getAttribute('data-mx-src');
        });
        var first = item.querySelector('[data-mx-mega-tile][data-mx-img]:not([data-mx-img=""])');
        if (first) preview(item, first, true);
      }

      function preview(item, tile, instant) {
        var img = item.querySelector('[data-mx-mega-img]');
        var title = item.querySelector('[data-mx-mega-title]');
        var src = tile.getAttribute('data-mx-img');
        if (title) title.textContent = tile.getAttribute('data-mx-title') || '';
        if (!img || !src || img.getAttribute('src') === src) return;
        if (instant || reduceMotion) { img.src = src; return; }
        img.classList.add('is-swapping');
        var next = new Image();
        next.onload = next.onerror = function () {
          img.src = src;
          img.classList.remove('is-swapping');
        };
        next.src = src;
      }

      function setOpen(item, open) {
        var link = item.querySelector('[data-mx-menu-link]');
        if (link) link.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) loadImages(item);
      }

      Array.prototype.forEach.call(menuItems, function (item) {
        var link = item.querySelector('[data-mx-menu-link]');

        /* Hover intent: open at once, close only after a short grace
           period, so a slightly off-line move down to the panel (or a
           brush past the edge) does not snap it shut. Entering another
           menu switches straight to it. */
        var closeTimer = null;
        item.addEventListener('mouseenter', function () {
          if (coarse.matches) return;
          clearTimeout(closeTimer);
          Array.prototype.forEach.call(menuItems, function (other) {
            if (other === item || !other.classList.contains('is-open')) return;
            other.classList.remove('is-open');
            other.classList.add('is-leaving');
            setOpen(other, false);
            setTimeout(function () { other.classList.remove('is-leaving'); }, 50);
          });
          item.classList.remove('is-leaving');
          item.classList.add('is-open');
          setOpen(item, true);
        });
        item.addEventListener('mouseleave', function () {
          if (coarse.matches) return;
          item.classList.remove('is-suppressed');
          clearTimeout(closeTimer);
          closeTimer = setTimeout(function () {
            item.classList.remove('is-open');
            if (!item.contains(document.activeElement)) setOpen(item, false);
          }, 280);
        });
        item.addEventListener('focusin', function (e) {
          /* Tabbing on into the panel after Escape brings it back */
          if (e.target !== link) item.classList.remove('is-suppressed');
          setOpen(item, true);
        });
        item.addEventListener('focusout', function () {
          setTimeout(function () {
            if (!item.contains(document.activeElement)) {
              item.classList.remove('is-suppressed', 'is-open');
              setOpen(item, false);
            }
          }, 0);
        });

        Array.prototype.forEach.call(item.querySelectorAll('[data-mx-mega-tile]'), function (tile) {
          tile.addEventListener('mouseenter', function () { preview(item, tile); });
          tile.addEventListener('focusin', function () { preview(item, tile); });
        });

        /* Touch: the first tap opens the panel, a second tap follows the link */
        if (link) {
          link.addEventListener('click', function (e) {
            if (!coarse.matches || item.classList.contains('is-open')) return;
            e.preventDefault();
            Array.prototype.forEach.call(menuItems, function (other) {
              if (other !== item) { other.classList.remove('is-open'); setOpen(other, false); }
            });
            item.classList.add('is-open');
            setOpen(item, true);
          });
        }
      });

      if (menuItems.length) {
        root.addEventListener('keydown', function (e) {
          if (e.key !== 'Escape') return;
          Array.prototype.forEach.call(menuItems, function (item) {
            if (!item.contains(document.activeElement) && !item.matches(':hover')) return;
            item.classList.remove('is-open');
            item.classList.add('is-suppressed');
            setOpen(item, false);
            var link = item.querySelector('[data-mx-menu-link]');
            if (link && item.contains(document.activeElement)) link.focus();
          });
        });
        document.addEventListener('click', function (e) {
          Array.prototype.forEach.call(menuItems, function (item) {
            if (!item.contains(e.target) && item.classList.contains('is-open')) {
              item.classList.remove('is-open');
              setOpen(item, false);
            }
          });
        });
      }

      /* ── Search ────────────────────────────────────────────────────── */
      /* Results open in the dropdown and never send the visitor to /search.
         Two sources feed it:
           - Categories: every collection, rendered into the page by
             mx-home-nav as JSON and matched here on title, handle and the
             merchant's keyword list (so "jackets" finds outerwear), instantly
             and with no request.
           - Products: Shopify predictive search (/search/suggest.json).
         With JS off the form is still a real GET to /search. */
      var form = root.querySelector('[data-mx-search]');
      if (!form) return;

      var input = form.querySelector('[data-mx-search-input]');
      var panel = form.querySelector('[data-mx-search-panel]');
      if (!input || !panel) return;

      /* "outerwear: jacket, jackets, coat" per line → { outerwear: [...] } */
      function parseAliases(text) {
        var map = {};
        text.split(/\r?\n/).forEach(function (line) {
          var at = line.indexOf(':');
          if (at < 1) return;
          var handle = line.slice(0, at).trim().toLowerCase();
          map[handle] = line.slice(at + 1).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        });
        return map;
      }

      function words(s) {
        return String(s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
      }

      var cats = [];
      var dataEl = form.querySelector('[data-mx-search-data]');
      if (dataEl) {
        try {
          var data = JSON.parse(dataEl.textContent);
          var aliases = parseAliases(data.aliases || '');
          cats = (data.cats || []).filter(function (c) { return c.count > 0; }).map(function (c) {
            return {
              title: c.title,
              url: c.url,
              image: c.image || '',
              count: c.count,
              names: words(c.title + ' ' + c.handle),
              alias: (aliases[c.handle] || []).map(words)
            };
          });
        } catch (err) { cats = []; }
      }

      /* Every query word must start some word of the name. The query is also
         tried without a trailing "s"/"es", so "schools" still finds "School". */
      function hits(list, q) {
        return q.every(function (t) {
          var bare = t.length > 3 ? t.replace(/e?s$/, '') : t;
          return list.some(function (w) { return w.indexOf(t) === 0 || w.indexOf(bare) === 0; });
        });
      }

      function matchCats(query) {
        var q = words(query);
        if (!q.length) return [];
        var scored = [];
        cats.forEach(function (c) {
          var score = 0;
          if (hits(c.names, q)) score = 3;
          else if (c.alias.some(function (a) { return hits(a, q); })) score = 2;
          if (score) scored.push({ c: c, score: score });
        });
        scored.sort(function (a, b) { return b.score - a.score || b.c.count - a.c.count; });
        return scored.slice(0, 4).map(function (s) { return s.c; });
      }

      /* An empty field: the button just focuses it (and opens it on mobile) */
      var searchButton = form.querySelector('.mx-search__go');
      if (searchButton) {
        searchButton.addEventListener('click', function (e) {
          if (!input.value.trim()) {
            e.preventDefault();
            input.focus();
          }
        });
      }

      var timer = null;
      var controller = null;
      var cursor = -1;
      var lastCats = [];
      var lastProducts = null;

      function close() {
        panel.hidden = true;
        panel.textContent = '';
        form.classList.remove('mx-search--open');
        input.setAttribute('aria-expanded', 'false');
        cursor = -1;
      }

      function open() {
        panel.hidden = false;
        form.classList.add('mx-search--open');
        input.setAttribute('aria-expanded', 'true');
      }

      function rows() {
        return panel.querySelectorAll('.mx-search__row');
      }

      /* Moves the arrow-key highlight. aria-selected is both the style hook
         and what a screen reader reads, so there is only one state to keep. */
      function move(delta) {
        var list = rows();
        if (!list.length) return;
        cursor = (cursor + delta + list.length) % list.length;
        Array.prototype.forEach.call(list, function (r, n) {
          r.setAttribute('aria-selected', n === cursor ? 'true' : 'false');
        });
        list[cursor].scrollIntoView({ block: 'nearest' });
      }

      function el(tag, cls, text) {
        var node = document.createElement(tag);
        if (cls) node.className = cls;
        /* textContent — titles are store data, not markup */
        if (text != null) node.textContent = text;
        return node;
      }

      function row(item, kind) {
        var a = el('a', 'mx-search__row mx-search__row--' + kind);
        a.href = item.url;
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');

        var thumb;
        if (item.image) {
          thumb = el('img', 'mx-search__thumb');
          thumb.src = item.image;
          thumb.alt = '';
          thumb.loading = 'lazy';
          thumb.width = 40;
          thumb.height = 40;
        } else {
          thumb = el('span', 'mx-search__thumb mx-search__thumb--blank');
          thumb.setAttribute('aria-hidden', 'true');
        }
        a.appendChild(thumb);

        var text = el('span', 'mx-search__text');
        text.appendChild(el('span', 'mx-search__label', item.title));
        if (kind === 'cat') {
          text.appendChild(el('span', 'mx-search__meta',
            'Category' + (item.count ? ' · ' + item.count + ' products' : '')));
        }
        a.appendChild(text);

        var arrow = el('span', 'mx-search__arrow', '→');
        arrow.setAttribute('aria-hidden', 'true');
        a.appendChild(arrow);
        return a;
      }

      function group(title, items, kind) {
        var box = el('div', 'mx-search__group');
        box.appendChild(el('p', 'mx-search__heading', title));
        items.forEach(function (item) { box.appendChild(row(item, kind)); });
        panel.appendChild(box);
      }

      /* lastProducts === null means the product request is still in flight */
      function render() {
        panel.textContent = '';
        cursor = -1;

        if (lastCats.length) group('Categories', lastCats, 'cat');

        if (lastProducts === null) {
          panel.appendChild(el('p', 'mx-search__status', 'Searching products…'));
        } else if (lastProducts.length) {
          group('Products', lastProducts, 'product');
        } else if (!lastCats.length) {
          panel.appendChild(el('p', 'mx-search__empty', 'No matches. Try a category like events, school or jackets.'));
        }
        open();
      }

      function search(q) {
        lastCats = matchCats(q);
        lastProducts = null;
        render();

        /* Drop the previous request so a slow early keystroke cannot land
           after a fast later one and overwrite newer results. */
        if (controller) controller.abort();
        controller = ('AbortController' in window) ? new AbortController() : null;

        var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                  '&resources[type]=product,collection' +
                  '&resources[limit]=6' +
                  '&resources[limit_scope]=each' +
                  '&resources[options][unavailable_products]=last';

        fetch(url, controller ? { signal: controller.signal } : undefined)
          .then(function (res) { return res.json(); })
          .then(function (data) {
            var r = (data && data.resources && data.resources.results) || {};

            /* Shopify's own collection hits are merged in after the local ones */
            var seen = {};
            lastCats.forEach(function (c) { seen[c.url.split('?')[0]] = 1; });
            (r.collections || []).forEach(function (c) {
              var bare = c.url.split('?')[0];
              if (seen[bare] || lastCats.length >= 4) return;
              seen[bare] = 1;
              lastCats.push({
                title: c.title,
                url: c.url,
                image: c.featured_image && c.featured_image.url ? c.featured_image.url : '',
                count: 0
              });
            });

            lastProducts = (r.products || []).slice(0, 6).map(function (p) {
              return {
                title: p.title,
                url: p.url,
                image: p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '')
              };
            });
            render();
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            lastProducts = [];
            render();
          });
      }

      input.addEventListener('input', function () {
        var q = input.value.trim();
        if (timer) clearTimeout(timer);

        if (q.length < 2) { close(); return; }
        /* Debounced so a normal typing burst is one request, not eight */
        timer = setTimeout(function () { search(q); }, 200);
      });

      /* Enter or the button: open the highlighted result, otherwise show the
         results right here. Never navigates to /search. */
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var list = rows();
        if (cursor > -1 && list[cursor]) { window.location.href = list[cursor].href; return; }
        var q = input.value.trim();
        if (!q) { input.focus(); return; }
        if (timer) clearTimeout(timer);
        search(q);
        input.focus();
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(); input.blur(); return; }
        if (panel.hidden) return;

        if (e.key === 'ArrowDown') { move(1); e.preventDefault(); }
        else if (e.key === 'ArrowUp') { move(-1); e.preventDefault(); }
      });

      input.addEventListener('focus', function () {
        var q = input.value.trim();
        if (q.length < 2) return;
        if (panel.textContent) open();
        else search(q);
      });

      document.addEventListener('click', function (e) {
        if (!form.contains(e.target)) close();
      });
      form.addEventListener('focusout', function () {
        /* Wait a tick — focus is briefly on nothing while it moves between
           the input and a result row. */
        setTimeout(function () {
          if (!form.contains(document.activeElement)) close();
        }, 0);
      });
    });
  }

  /* ========================================================================
     HERO — slideshow with dots, autoplay, hover/focus pause, arrow keys
     ====================================================================== */
  function initHero(scope) {
    each(scope, '[data-mx-hero]', function (root) {
      if (!claim(root, 'Hero')) return;

      var slides = root.querySelectorAll('[data-mx-hero-slide]');
      var dots = root.querySelectorAll('[data-mx-hero-dot]');
      if (slides.length < 2) return;

      var delay = parseInt(root.dataset.mxInterval, 10) || 6000;
      var index = 0;
      var timer = null;

      function go(next) {
        if ((next + slides.length) % slides.length !== index) {
          slides[0].removeAttribute('data-mx-intro-landed');
        }
        index = (next + slides.length) % slides.length;
        slides.forEach(function (s, n) {
          s.classList.toggle('mx-hero__slide--on', n === index);
          /* Keep the offscreen slides out of the tab order and the a11y tree */
          s.setAttribute('aria-hidden', n === index ? 'false' : 'true');
        });
        dots.forEach(function (d, n) {
          if (n === index) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }

      function play() {
        stop();
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.hidden || (window.MXIntro && window.MXIntro.active) ||
            root.matches(':hover') || root.contains(document.activeElement)) return;
        timer = setInterval(function () { go(index + 1); }, delay);
      }

      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      dots.forEach(function (dot, n) {
        dot.addEventListener('click', function () {
          go(n);
          play();
        });
      });

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', play);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', play);

      root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { go(index + 1); play(); }
        else if (e.key === 'ArrowLeft') { go(index - 1); play(); }
        else return;
        e.preventDefault();
      });

      /* Stop the timer while the tab is hidden so slides do not race ahead */
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else play();
      });

      document.addEventListener('mx:intro:complete', play, { once: true });
      go(0);
      play();
    });
  }

  /* ========================================================================
     CAROUSEL — products and reviews
     Native scroll-snap does the scrolling; the arrows just nudge it by one
     card and reflect whether there is anywhere left to go.
     ====================================================================== */
  function initCarousels(scope) {
    each(scope, '[data-mx-carousel]', function (root) {
      if (!claim(root, 'Carousel')) return;

      var track = root.querySelector('[data-mx-track]');
      /* Look in the whole section, not just inside .mx-carousel. The Liquid
         for both carousels already places the buttons in a
         .mx-carousel__nav up in the heading, so a lookup scoped to
         .mx-carousel finds nothing and the arrows never get click handlers
         — they render in the right place but do nothing. Falling back to
         the parent wires up either arrangement. */
      var section = root.parentElement || root;
      var prev = root.querySelector('[data-mx-prev]') || section.querySelector('[data-mx-prev]');
      var next = root.querySelector('[data-mx-next]') || section.querySelector('[data-mx-next]');
      if (!track) return;

      /* The arrows move up into the section heading's row: off the cards,
         and out of the track's way so the cards keep the page margins.
         Skipped when the markup already put them there. */
      var head = root.parentElement.querySelector('.mx-prods__head, .mx-rev__head');
      if (head && prev && next && !prev.closest('.mx-carousel__nav')) {
        var navBox = document.createElement('div');
        navBox.className = 'mx-carousel__nav';
        navBox.appendChild(prev);
        navBox.appendChild(next);
        head.appendChild(navBox);
      }

      function step() {
        var card = track.firstElementChild;
        if (!card) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        return card.getBoundingClientRect().width + gap;
      }

      /* ── True infinite loop ────────────────────────────────────────
         Previously the ends were handled by rewinding: at the last card,
         "next" did scrollTo(0) and the row flew back to the beginning.
         That reads as going backwards, not as looping.

         Instead the real cards are duplicated once before and once after
         themselves, so there is no reachable end in either direction.
         Whenever the scroll position drifts into one of the copies, the
         position is shifted by exactly one set-width. The copies are
         identical to the originals, so that shift is invisible — the row
         just keeps advancing: 1234 -> 2345 -> 3451 -> 4512.

         Cloning rather than moving DOM nodes keeps native scrolling and
         touch swiping working untouched. */
      var loopOn = false;
      var realCount = 0;
      /* Index this carousel is travelling towards while a smooth scroll is
         still running. Without it, a second click read the half-finished
         position, rounded back to the card it had just left, and the row
         stalled on the same card for a click. */
      var pendingIndex = null;

      function clearClones() {
        Array.prototype.slice.call(track.querySelectorAll('[data-mx-clone]'))
          .forEach(function (el) { el.remove(); });
      }

      /* A copy is only ever a mirror of a card that is already on screen, so it
         must never carry reveal state.

         The scroll-reveal sets an inline "from" state on each card — opacity 0,
         nudged down and scaled slightly — and clears it once that card animates
         in. It collects its targets once, up front, so a copy made before the
         originals have finished revealing inherits that from-state and nothing
         ever animates it away: the copy keeps its space in the row but stays
         invisible. That is what made the row look empty past the last real
         product instead of looping straight back to the first. */
      function deanimate(el) {
        var nodes = [el].concat(Array.prototype.slice.call(el.querySelectorAll('[style], [data-mx-gs]')));
        nodes.forEach(function (n) {
          n.removeAttribute('style');
          n.removeAttribute('data-mx-gs');
          n.removeAttribute('data-mx-reveal');
          Array.prototype.slice.call(n.classList).forEach(function (cls) {
            if (cls.indexOf('mx-reveal') === 0) n.classList.remove(cls);
          });
        });
      }

      /* Position in the track's content, independent of the current scroll.

         offsetLeft, not getBoundingClientRect(): the rect is where the card is
         *painted*, so it moves with the scroll-reveal's transform while that
         animation is still running, and every edge measured during it comes
         back wrong — which is how a click could land two cards along instead
         of one. offsetLeft is the laid-out position and ignores transforms
         entirely. .mx-carousel__track is position: relative so it is the
         offsetParent, which makes this measured from its padding edge — the
         same origin scrollLeft counts from. */
      function contentDistance(el) {
        return el.offsetLeft;
      }

      /* Repositioning inside the copies has to be instant. The track sets
         scroll-behavior: smooth in CSS, which applies to assigning scrollLeft
         too, so the jump that is supposed to be invisible animated instead —
         the row visibly slid backwards, and anything measured before it
         finished read a half-way position. */
      function jumpTo(x) {
        var prev = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = x;
        track.style.scrollBehavior = prev;
      }

      function edgeAt(i) {
        var el = track.children[i];
        return el ? contentDistance(el) : 0;
      }

      function setupLoop() {
        clearClones();
        loopOn = false;
        pendingIndex = null;

        var real = Array.prototype.slice.call(track.children);
        /* Nothing to loop through if the row already fits, or there is only
           one card — it would just judder in place. */
        if (real.length < 2 || track.scrollWidth <= track.clientWidth + 2) return;

        function copyOf(card) {
          var c = card.cloneNode(true);
          c.setAttribute('data-mx-clone', '');
          c.setAttribute('aria-hidden', 'true');
          deanimate(c);
          return c;
        }

        var firstReal = track.firstElementChild;
        real.forEach(function (card) { track.insertBefore(copyOf(card), firstReal); });
        real.forEach(function (card) { track.appendChild(copyOf(card)); });

        realCount = real.length;
        loopOn = true;
        jumpTo(edgeAt(realCount));
      }

      /* Which card the row is currently parked on — the nearest edge, so it
         also answers correctly after a touch swipe or trackpad scroll, which
         land wherever scroll-snap puts them rather than on a click target. */
      function currentIndex() {
        var cur = track.scrollLeft;
        var best = 0;
        var bestDist = Infinity;
        for (var i = 0; i < track.children.length; i++) {
          var d = Math.abs(contentDistance(track.children[i]) - cur);
          if (d < bestDist) { bestDist = d; best = i; }
        }
        return best;
      }

      /* Keeps the position inside the middle (real) set. Called once scrolling
         stops, so it covers arrows, wheel and touch alike.

         The shift is measured between the two identical cards rather than by a
         fixed set-width: the gap where one set meets the next is not the same
         as the gaps inside a set (393px inside, 388px and 399px at the seams
         on this layout), so a fixed width drifts a few pixels per lap until a
         whole card falls out of the cycle. Card-to-card is exact. */
      function rewrap() {
        if (!loopOn) return;
        pendingIndex = null;

        var i = currentIndex();
        var j = i;
        if (i < realCount) j = i + realCount;
        else if (i >= realCount * 2) j = i - realCount;
        if (j === i) return;

        jumpTo(track.scrollLeft + edgeAt(j) - edgeAt(i));
      }

      function atEnd() { return track.scrollLeft >= track.scrollWidth - track.clientWidth - 4; }
      function atStart() { return track.scrollLeft <= 4; }

      function go(dir) {
        var smooth = reduceMotion ? 'auto' : 'smooth';

        if (loopOn) {
          /* Step by one card index and scroll to that card's exact edge.
             scrollBy + scroll-snap lands a few pixels off each time and the
             error accumulates until a card gets skipped; an edge is exact, so
             every click advances by precisely one card and the loop stays in
             phase: 1234 -> 2345 -> 3451 -> 4512. */
          var base = pendingIndex === null ? currentIndex() : pendingIndex;
          var i = base + dir;
          if (i < 0) i = 0;
          if (i > track.children.length - 1) i = track.children.length - 1;
          pendingIndex = i;
          track.scrollTo({ left: edgeAt(i), behavior: smooth });
          return;
        }

        if (dir > 0 && atEnd()) track.scrollTo({ left: 0, behavior: smooth });
        else if (dir < 0 && atStart()) track.scrollTo({ left: track.scrollWidth, behavior: smooth });
        else track.scrollBy({ left: dir * step(), behavior: smooth });
      }

      /* Debounced until scrolling stops. Repositioning mid-flight fights the
         browser's own smooth-scroll animation: it keeps travelling toward a
         target that has just moved, and the row stalls on the same card. */
      var wrapTimer = null;
      track.addEventListener('scroll', function () {
        if (wrapTimer) clearTimeout(wrapTimer);
        wrapTimer = setTimeout(rewrap, 170);
      }, { passive: true });

      window.addEventListener('resize', function () {
        if (!loopOn) return;
        pendingIndex = null;
        jumpTo(edgeAt(realCount));
      });

      /* The reviews carousel swaps its cards in after the Google fetch and
         fires a resize to say so — rebuild the copies around the new set. */
      root.addEventListener('mx:cards-changed', setupLoop);

      setupLoop();
      if (document.readyState !== 'complete') window.addEventListener('load', setupLoop);

      if (prev) prev.addEventListener('click', function () { go(-1); pauseFor(9000); });
      if (next) next.addEventListener('click', function () { go(1); pauseFor(9000); });

      /* ── Autoplay ───────────────────────────────────────────────────
         Advances one card every few seconds. It holds while the pointer
         is over the carousel, while anything inside has keyboard focus,
         for a while after a touch or arrow press, whenever the carousel
         is off screen or the tab is hidden, and never runs at all with
         reduced motion. */
      var DELAY = parseInt(root.getAttribute('data-mx-autoplay'), 10) || 4500;
      var timer = null;
      var hovering = false, focused = false, onScreen = false, heldUntil = 0;

      function canPlay() {
        return !reduceMotion && onScreen && !hovering && !focused && !document.hidden &&
               Date.now() >= heldUntil && track.scrollWidth > track.clientWidth + 4;
      }
      function tick() {
        timer = null;
        if (canPlay()) go(1);
        schedule();
      }
      function schedule() {
        if (timer || reduceMotion) return;
        timer = window.setTimeout(tick, Math.max(DELAY, heldUntil - Date.now()));
      }
      function pauseFor(ms) { heldUntil = Date.now() + ms; }

      root.addEventListener('mouseenter', function () { hovering = true; });
      root.addEventListener('mouseleave', function () { hovering = false; pauseFor(1500); });
      root.addEventListener('focusin', function () { focused = true; });
      root.addEventListener('focusout', function (e) { if (!root.contains(e.relatedTarget)) focused = false; });
      track.addEventListener('pointerdown', function () { pauseFor(9000); }, { passive: true });
      track.addEventListener('wheel', function () { pauseFor(9000); }, { passive: true });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
          if (onScreen) { pauseFor(1500); schedule(); }
        }, { threshold: 0.35 }).observe(root);
      } else {
        onScreen = true;
      }
      schedule();
    });
  }

  /* ========================================================================
     INDUSTRIES — image accordion
     ====================================================================== */
  function initAccordion(scope) {
    each(scope, '[data-mx-acc]', function (root) {
      if (!claim(root, 'Acc')) return;

      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-mx-panel]'));
      if (!panels.length) return;

      var activeIndex = 0;
      var narrow = window.matchMedia('(max-width: 900px)');
      var offsets = [0, 54, 90.5, 117, 155];
      var tones = ['#eeeeee', '#e5e5e5', '#d8d8d8', '#cdcdcd', '#bcbcbc'];

      function render() {
        var leftTabs = Math.min(activeIndex, 3);
        panels.forEach(function (panel, index) {
          var distance = index - activeIndex;
          var visible = distance >= -3 && distance <= 4;
          var x;
          if (distance < 0) {
            /* Earlier cards leave narrow, clickable tabs on the left. */
            x = distance >= -3 ? -100 + (leftTabs + distance + 1) * 8 : -115;
          } else {
            x = leftTabs * 8 + (offsets[distance] === undefined ? 160 + distance * 12 : offsets[distance]);
          }
          var outside = !visible && !narrow.matches;
          panel.classList.toggle('is-outside', outside);
          panel.inert = outside;
          panel.setAttribute('aria-hidden', outside ? 'true' : 'false');
          panel.setAttribute('aria-expanded', narrow.matches || index === activeIndex ? 'true' : 'false');
          panel.style.setProperty('--mx-panel-x', x + '%');
          panel.style.setProperty('--mx-panel-layer', String(panels.length - index));
          panel.style.setProperty('--mx-panel-bg', tones[Math.max(0, Math.min(distance, 4))]);
        });
      }

      function open(panel) {
        var index = panels.indexOf(panel);
        if (index < 0) return;
        activeIndex = index;
        render();
      }

      /* A tap on the panel that is already open goes to its collection; on
         phones (the slider) every card is "open", so any tap goes through. */
      panels.forEach(function (panel) {
        panel.addEventListener('click', function () {
          var href = panel.getAttribute('data-href');
          if (href && (narrow.matches || panels.indexOf(panel) === activeIndex)) {
            window.location.href = href;
            return;
          }
          open(panel);
        });
        panel.addEventListener('focus', function () { if (!narrow.matches) open(panel); });
      });

      /* A moving card must not trigger another hover under a stationary cursor. */
      var pointerX = null;
      var pointerY = null;
      root.addEventListener('pointermove', function (e) {
        if (!window.matchMedia('(hover: hover) and (min-width: 901px)').matches) return;
        if (e.clientX === pointerX && e.clientY === pointerY) return;
        pointerX = e.clientX;
        pointerY = e.clientY;
        var panel = e.target.closest('[data-mx-panel]');
        if (panel && root.contains(panel) && panels.indexOf(panel) !== activeIndex) open(panel);
      });
      root.addEventListener('pointerleave', function () { pointerX = null; pointerY = null; });

      /* ── Phone slider (<=900px) ─────────────────────────────────────
         CSS turns the row into a horizontal scroll-snap strip. Here: dots,
         arrows that scroll it, and tracking which card is in front. */
      var headEl = root.parentElement.querySelector('.mx-ind__head');
      var navEl = headEl && headEl.querySelector('.mx-ind__nav');
      var controls = document.createElement('div');
      controls.className = 'mx-ind__controls';
      var dotsEl = document.createElement('div');
      dotsEl.className = 'mx-ind__dots';
      dotsEl.setAttribute('role', 'group');
      dotsEl.setAttribute('aria-label', 'Choose an industry');
      var dots = panels.map(function (panel, n) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mx-ind__dot';
        var title = panel.querySelector('.mx-ind__title');
        dot.setAttribute('aria-label', 'Show ' + (title ? title.textContent.trim() : 'industry ' + (n + 1)));
        dot.addEventListener('click', function () { slideTo(n); });
        dotsEl.appendChild(dot);
        return dot;
      });
      controls.appendChild(dotsEl);
      root.insertAdjacentElement('afterend', controls);

      function slideLeft(n) {
        return panels[n].offsetLeft - parseFloat(getComputedStyle(root).paddingLeft || 0);
      }
      function slideTo(n) {
        n = (n + panels.length) % panels.length;
        root.scrollTo({ left: slideLeft(n), behavior: reduceMotion ? 'auto' : 'smooth' });
        markDot(n);
      }
      function markDot(n) {
        activeIndex = n;
        dots.forEach(function (d, i) {
          if (i === n) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }
      var ticking = false;
      root.addEventListener('scroll', function () {
        if (!narrow.matches || ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var best = 0, bestDist = Infinity;
          panels.forEach(function (p, i) {
            var d = Math.abs(slideLeft(i) - root.scrollLeft);
            if (d < bestDist) { bestDist = d; best = i; }
          });
          /* At the very end the last card cannot snap to the left edge */
          if (root.scrollLeft + root.clientWidth >= root.scrollWidth - 4) best = panels.length - 1;
          if (best !== activeIndex) markDot(best);
        });
      }, { passive: true });

      /* The arrows live in the heading on desktop and under the slider on
         phones, beside the dots, where a thumb can reach them. */
      function placeNav() {
        if (!navEl) return;
        if (narrow.matches) controls.appendChild(navEl);
        else headEl.appendChild(navEl);
        if (narrow.matches) { markDot(Math.min(activeIndex, panels.length - 1)); }
      }
      if (narrow.addEventListener) narrow.addEventListener('change', placeNav);
      else narrow.addListener(placeNav);
      placeNav();

      function step(delta) {
        if (narrow.matches) { slideTo(activeIndex + delta); return; }
        activeIndex = (activeIndex + delta + panels.length) % panels.length;
        render();
      }

      var head = root.parentElement;
      var prevBtn = head && head.querySelector('[data-mx-acc-prev]');
      var nextBtn = head && head.querySelector('[data-mx-acc-next]');
      if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

      root.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        if (panels.indexOf(document.activeElement) < 0) return;
        e.preventDefault();
        step(e.key === 'ArrowRight' ? 1 : -1);
        panels[activeIndex].focus({ preventScroll: narrow.matches });
      });

      window.addEventListener('resize', render);
      render();
    });
  }

  /* ========================================================================
     PROCESS — the row plays through once when it comes into view
     The step names live on the tiles as data attributes and surface on the
     caption line, which is what the design shows instead of five labels.
     ====================================================================== */
  function initProcess(scope) {
    each(scope, '[data-mx-proc]', function (root) {
      if (!claim(root, 'Proc')) return;

      var steps = Array.prototype.slice.call(root.querySelectorAll('[data-mx-step]'));
      if (!steps.length) return;

      var narrow = window.matchMedia('(max-width: 900px)');
      var caption = root.parentElement && root.parentElement.querySelector('[data-mx-proc-caption]');
      var timer = null;

      function clear() {
        steps.forEach(function (el) { el.classList.remove('is-on'); });
      }

      function show(i) {
        clear();
        var el = steps[i];
        if (!el) return;
        el.classList.add('is-on');
        if (!caption) return;
        caption.innerHTML = '';
        var num = document.createElement('b');
        num.textContent = el.dataset.mxNum || '';
        caption.appendChild(num);
        caption.appendChild(document.createTextNode(el.dataset.mxLabel || ''));
        caption.classList.add('is-on');
      }

      function rest() {
        clear();
        if (caption) {
          caption.classList.remove('is-on');
          caption.textContent = '';
        }
      }

      function play() {
        window.clearInterval(timer);
        var i = 0;
        show(0);
        timer = window.setInterval(function () {
          i += 1;
          if (i >= steps.length) {
            window.clearInterval(timer);
            /* Hold the last step briefly, then hand the row back to hover. */
            window.setTimeout(rest, 1100);
            return;
          }
          show(i);
        }, 900);
      }

      /* Pointer and keyboard both drive the same highlight — desktop only.
         On a phone the row auto-scrolls itself; see below. */
      steps.forEach(function (el, i) {
        function take() {
          if (narrow.matches) return;
          window.clearInterval(timer);
          show(i);
        }
        el.addEventListener('mouseenter', take);
        el.addEventListener('focus', take);
      });
      root.addEventListener('mouseleave', function () { if (!narrow.matches) rest(); });
      root.addEventListener('focusout', function (e) {
        if (!narrow.matches && !root.contains(e.relatedTarget)) rest();
      });

      /* Plays once, the first time the row is on screen — desktop only. With
         reduced motion the sequence is skipped — the row is decorative, and
         the step names are already in the markup. */
      if (!reduceMotion && 'IntersectionObserver' in window) {
        var playIo = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting || narrow.matches) return;
          playIo.disconnect();
          window.setTimeout(play, 350);
        }, { threshold: .45 });
        playIo.observe(root);
      }

      /* ── Phone slider (<=900px) ────────────────────────────────────────
         CSS turns the row into a horizontal scroll-snap strip. Arrows and
         autoplay step between real steps by their own offsetLeft, not by
         DOM-child index — the row still carries the (hidden) chevrons as
         real children for the desktop layout above, and index-based
         stepping would land on one every other click. Autoplay starts 3s
         after the row first comes on screen and advances every 2.5s,
         wrapping back to the first step after the last — paused by a touch,
         a hovering pointer, keyboard focus, the tab going hidden, or
         reduced motion. */
      var prevBtn = root.parentElement && root.parentElement.querySelector('[data-mx-proc-prev]');
      var nextBtn = root.parentElement && root.parentElement.querySelector('[data-mx-proc-next]');

      function slideTo(i) {
        i = Math.max(0, Math.min(i, steps.length - 1));
        root.scrollTo({ left: steps[i].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      function currentSlide() {
        var left = root.scrollLeft;
        var maxLeft = root.scrollWidth - root.clientWidth;
        /* The last step's target position can exceed how far the row actually
           scrolls, so the browser clamps it - nearest-offsetLeft matching would
           then read that clamped position as the SECOND-to-last step forever,
           and advance() could never reach (or exceed) steps.length to wrap.
           Checking the scroll extremes directly avoids that, the same way the
           shared carousel's atEnd()/atStart() do. */
        if (left >= maxLeft - 2) return steps.length - 1;
        if (left <= 2) return 0;
        var best = 0, bestDist = Infinity;
        steps.forEach(function (el, i) {
          var d = Math.abs(el.offsetLeft - left);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      }

      var autoTimer = null;
      var startTimer = null;
      var heldUntil = 0;
      var onScreen = false;
      var focused = false;

      function pauseFor(ms) { heldUntil = Date.now() + ms; }
      function canAuto() {
        return narrow.matches && !reduceMotion && onScreen && !focused &&
               !document.hidden && Date.now() >= heldUntil;
      }
      function scheduleAuto() {
        if (autoTimer || !narrow.matches || reduceMotion) return;
        autoTimer = window.setTimeout(advance, Math.max(2500, heldUntil - Date.now()));
      }
      function advance() {
        autoTimer = null;
        if (canAuto()) {
          var next = currentSlide() + 1;
          slideTo(next >= steps.length ? 0 : next);
        }
        scheduleAuto();
      }
      function beginAuto() {
        window.clearTimeout(startTimer);
        /* The first advance fires directly at 3s; scheduleAuto (2.5s) only
           governs the interval between the ones after it. Routing the first
           one through scheduleAuto too, as an earlier version did, stacked its
           own 2.5s on top and made the real first delay 5.5s instead of 3s. */
        startTimer = window.setTimeout(advance, 3000);
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { slideTo(currentSlide() - 1); pauseFor(6000); });
      if (nextBtn) nextBtn.addEventListener('click', function () { slideTo(currentSlide() + 1); pauseFor(6000); });
      root.addEventListener('pointerdown', function () { pauseFor(6000); }, { passive: true });
      root.addEventListener('wheel', function () { pauseFor(6000); }, { passive: true });
      root.addEventListener('focusin', function () { focused = true; });
      root.addEventListener('focusout', function (e) { if (!root.contains(e.relatedTarget)) focused = false; });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          var was = onScreen;
          onScreen = entries[0].isIntersecting;
          if (onScreen && !was) beginAuto();
        }, { threshold: .35 }).observe(root);
      }
    });
  }

  /* ========================================================================
     REVIEWS — live Google reviews
     Same Cloudflare Worker the existing google-reviews section uses. The
     server-rendered cards stay put if the fetch fails, so the section is
     never empty.
     ====================================================================== */
  function initReviews(scope) {
    each(scope, '[data-mx-reviews]', function (root) {
      if (!claim(root, 'Reviews')) return;

      var placeId = root.dataset.mxPlaceId;
      var track = root.querySelector('[data-mx-track]');
      if (!placeId || !track) return;

      var worker = 'https://bp-google-reviews.aurixlab.workers.dev/';

      function stars(rating) {
        var r = Math.round(Number(rating) || 0);
        var out = '';
        for (var i = 1; i <= 5; i++) {
          out += '<span class="' + (i > r ? 'mx-star--off' : '') + '">★</span>';
        }
        return out;
      }

      function card(review) {
        var el = document.createElement('article');
        el.className = 'mx-review';
        el.setAttribute('data-mx-review-name', (review.author_name || '').trim().toLowerCase());

        /* The initial-letter circle, used when there is no photo and as the
           fallback when one fails to load. */
        function initialAvatar() {
          var el2 = document.createElement('div');
          el2.className = 'mx-review__avatar';
          el2.setAttribute('aria-hidden', 'true');
          el2.textContent = (review.author_name || '?').trim().charAt(0).toUpperCase();
          return el2;
        }

        var avatar;
        if (review.profile_photo_url) {
          avatar = document.createElement('img');
          avatar.className = 'mx-review__avatar';
          avatar.src = review.profile_photo_url;
          avatar.alt = '';
          avatar.loading = 'lazy';
          avatar.referrerPolicy = 'no-referrer';
          avatar.width = 56;
          avatar.height = 56;
          /* Google's profile URLs are not guaranteed to resolve — they expire,
             404, or get rate limited, and a broken <img> leaves an empty
             circle with nothing in it. Swap in the initial instead. */
          avatar.onerror = function () {
            if (avatar.parentNode) avatar.parentNode.replaceChild(initialAvatar(), avatar);
          };
        } else {
          avatar = initialAvatar();
        }
        el.appendChild(avatar);

        /* textContent, not innerHTML — this copy comes from a third party */
        var text = document.createElement('p');
        text.className = 'mx-review__text';
        text.textContent = review.text || '';
        el.appendChild(text);

        var name = document.createElement('p');
        name.className = 'mx-review__name';
        name.textContent = review.author_name || '';
        el.appendChild(name);

        var rating = document.createElement('p');
        rating.className = 'mx-review__stars';
        rating.setAttribute('aria-label', (Number(review.rating) || 0) + ' out of 5 stars');
        rating.innerHTML = stars(review.rating);
        el.appendChild(rating);

        return el;
      }

      fetch(worker + '?placeId=' + encodeURIComponent(placeId), { cache: 'no-store' })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (!data || data.status !== 'OK' || !data.result) return;

          /* Only 5-star reviews that have something to read */
          var live = (Array.isArray(data.result.reviews) ? data.result.reviews : [])
            .filter(function (r) { return Number(r.rating) === 5 && (r.text || '').trim(); });

          /* Merge rather than replace: live reviews go first (newest), a
             rendered card for the same person is swapped for the live one
             (it brings their photo), and the rest of the rendered reviews
             stay behind them, so the slider keeps plenty of cards. */
          if (live.length) {
            /* The carousel loop duplicates the card set around itself. Those
               copies have to go before merging, or the anchor and the
               duplicate-name lookup below would both land on a copy instead
               of a real card. The loop is rebuilt from the new set after. */
            Array.prototype.slice.call(track.querySelectorAll('[data-mx-clone]'))
              .forEach(function (el) { el.remove(); });

            var anchor = track.firstElementChild;
            live.forEach(function (review) {
              var key = (review.author_name || '').trim().toLowerCase();
              var fresh = card(review);
              var old = key && track.querySelector('[data-mx-review-name="' + key.replace(/"/g, '') + '"]:not([data-mx-clone])');
              if (old) {
                if (old === anchor) anchor = old.nextElementSibling;
                old.remove();
              }
              track.insertBefore(fresh, anchor);
            });
            /* Instant: the carousel re-clones off the back of this, and a
               smooth animation still in flight would drag the row back off
               the position it settles on. */
            track.style.scrollBehavior = 'auto';
            track.scrollLeft = 0;
            track.style.scrollBehavior = '';

            /* Tell the carousel the card set changed so it re-clones. */
            var carousel = root.querySelector('[data-mx-carousel]');
            if (carousel) carousel.dispatchEvent(new CustomEvent('mx:cards-changed'));
          }

          /* Keep the rating and count in the proof section honest */
          if (data.result.rating) {
            each(document, '[data-mx-google-rating]', function (el) {
              el.textContent = Number(data.result.rating).toFixed(1);
            });
          }
          if (data.result.user_ratings_total) {
            each(document, '[data-mx-google-count]', function (el) {
              el.textContent = data.result.user_ratings_total;
            });
          }
        })
        .catch(function () {
          /* Offline, blocked, or the worker is down — the rendered cards stand. */
        });
    });
  }

  /* ========================================================================
     SAME DAY COUNTDOWN
     Same logic as the old theme header's #bp-timer: counts down to the
     11:00 AM Mountain (America/Denver) cut-off, Monday to Friday; after
     Friday's cut-off and over the weekend it counts to Monday 11:00.
     One interval drives every [data-mx-sameday-timer] (bar + drawer).
     ====================================================================== */
  var sameDayTicking = false;
  function initSameDay(scope) {
    var timers = (scope || document).querySelectorAll('[data-mx-sameday-timer]');
    if (!timers.length || sameDayTicking) return;
    sameDayTicking = true;

    function nowMST() {
      return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Denver' }));
    }

    function getNextEndTimeMST() {
      var now = nowMST();
      var end = new Date(now);
      end.setHours(11, 0, 0, 0);
      var day = now.getDay();

      if (day === 5 && now.getTime() < end.getTime()) {
        return end;
      } else if (day === 5 && now.getTime() >= end.getTime()) {
        end.setDate(end.getDate() + 3);
      } else if (day === 6 || day === 0) {
        end.setDate(end.getDate() + (1 + 7 - day) % 7);
      } else if (now.getTime() >= end.getTime()) {
        end.setDate(end.getDate() + 1);
      }
      return end;
    }

    var endTime = getNextEndTimeMST();

    function tick() {
      var now = nowMST();
      var diff = endTime - now;
      if (diff <= 0) {
        endTime = getNextEndTimeMST();
        diff = endTime - now;
      }
      var h = String(Math.floor(diff / 3600000)).padStart(2, '0');
      var m = String(Math.floor((diff / 60000) % 60)).padStart(2, '0');
      var s = String(Math.floor((diff / 1000) % 60)).padStart(2, '0');
      var text = h + ':' + m + ':' + s;
      /* Re-query so timers added later (section re-render in the editor) tick too */
      each(document, '[data-mx-sameday-timer]', function (el) { el.textContent = text; });
    }

    tick();
    setInterval(tick, 1000);
  }

  /* ========================================================================
     DECORATION SLIDER
     Phones only: the grid is a native scroll-snap row; JS just keeps the
     dots in step with the card in view and lets them jump to a card.
     ====================================================================== */
  function initDecoration(scope) {
    each(scope, '[data-mx-deco-slider]', function (slider) {
      if (!claim(slider, 'Decoration')) return;

      var cards = Array.prototype.slice.call(slider.querySelectorAll('[data-mx-deco-card]'));
      var dots = Array.prototype.slice.call(slider.parentElement.querySelectorAll('[data-mx-deco-dot]'));
      var frame = null;
      if (!cards.length || !dots.length) return;

      function update() {
        frame = null;
        var start = slider.getBoundingClientRect().left + parseFloat(getComputedStyle(slider).paddingLeft || 0);
        var nearest = 0;
        var distance = Infinity;
        cards.forEach(function (card, index) {
          var diff = Math.abs(card.getBoundingClientRect().left - start);
          if (diff < distance) { distance = diff; nearest = index; }
        });
        /* Scrolled to the end: the last card can't reach the snap line */
        if (slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 2) nearest = cards.length - 1;
        dots.forEach(function (dot, index) {
          dot.classList.toggle('is-active', index === nearest);
          dot.setAttribute('aria-current', index === nearest ? 'true' : 'false');
        });
      }

      function schedule() {
        if (frame === null) frame = window.requestAnimationFrame(update);
      }

      dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          if (!cards[index]) return;
          var pad = parseFloat(getComputedStyle(slider).paddingLeft || 0);
          var left = slider.scrollLeft + cards[index].getBoundingClientRect().left - slider.getBoundingClientRect().left - pad;
          slider.scrollTo({ left: left, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });
      slider.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      schedule();
    });
  }

  /* ========================================================================
     CATEGORY CAROUSEL
     Mobile scroll snap stays native; JS only tracks the centered card and dots.
     ====================================================================== */
  function initCategories(scope) {
    each(scope, '[data-mx-category-slider]', function (slider) {
      if (!claim(slider, 'Categories')) return;

      var cards = Array.prototype.slice.call(slider.querySelectorAll('[data-mx-category-card]'));
      var dots = Array.prototype.slice.call(slider.parentElement.querySelectorAll('[data-mx-category-dot]'));
      var mobile = window.matchMedia('(max-width: 700px)');
      var frame = null;
      if (!cards.length) return;

      function update() {
        frame = null;
        if (!mobile.matches) {
          slider.classList.remove('mx-cats__grid--ready');
          cards.forEach(function (card) { card.classList.remove('is-active'); });
          return;
        }
        var center = slider.getBoundingClientRect().left + slider.clientWidth / 2;
        var nearest = 0;
        var distance = Infinity;
        cards.forEach(function (card, index) {
          var bounds = card.getBoundingClientRect();
          var diff = Math.abs(bounds.left + bounds.width / 2 - center);
          if (diff < distance) { distance = diff; nearest = index; }
        });
        cards.forEach(function (card, index) {
          card.classList.toggle('is-active', index === nearest);
        });
        slider.classList.add('mx-cats__grid--ready');
        dots.forEach(function (dot, index) {
          dot.classList.toggle('is-active', index === nearest);
          dot.setAttribute('aria-current', index === nearest ? 'true' : 'false');
        });
      }

      function schedule() {
        if (frame === null) frame = window.requestAnimationFrame(update);
      }

      dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          var bounds = cards[index].getBoundingClientRect();
          var sliderBounds = slider.getBoundingClientRect();
          var left = slider.scrollLeft + bounds.left - sliderBounds.left - (slider.clientWidth - bounds.width) / 2;
          slider.scrollTo({ left: left, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });
      slider.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      schedule();
    });
  }

  /* ========================================================================
     SCROLL REVEAL
     Two engines, same targets:
       - GSAP + ScrollTrigger (theme.liquid already loads both site-wide):
         word-by-word heading reveals, staggered cards with image wipes,
         count-up numbers and a light hero parallax.
       - CSS + IntersectionObserver: the fallback if GSAP is missing, and
         always used for the category cards, whose own CSS builds on the
         .mx-in class.
     Nothing is hidden until JS runs, and nothing runs with reduced motion,
     so the page is never left blank.
     ====================================================================== */
  var STAGGER_CAP = 700;   // a long row never waits more than this (ms)

  /* Wraps each word in a mask so it can slide up from below. Walks child
     elements (b, span, em…) so the heading's own markup survives. */
  function splitWords(el) {
    if (el.dataset.mxSplit) return el.querySelectorAll('.mx-w__i');
    el.dataset.mxSplit = '1';
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var parts = child.textContent.split(/(\s+)/);
          if (parts.length === 1 && !parts[0].trim()) return;
          var frag = document.createDocumentFragment();
          parts.forEach(function (part) {
            if (!part) return;
            if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
            var mask = document.createElement('span');
            mask.className = 'mx-w';
            var inner = document.createElement('span');
            inner.className = 'mx-w__i';
            inner.textContent = part;
            mask.appendChild(inner);
            frag.appendChild(mask);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR' && child.tagName !== 'SVG') {
          walk(child);
        }
      });
    })(el);
    var words = el.querySelectorAll('.mx-w__i');
    Array.prototype.forEach.call(words, function (w, n) { w.style.setProperty('--i', n); });
    return words;
  }

  /* ── CSS engine (fallback, and the category cards) ─────────────────── */
  var CSS_SINGLES = [
    ['.mx-h2, .mx-proof__head', 'rise'],
    ['.mx-rebrand__card', 'zoom'],
    ['.mx-ind__acc, .mx-cats__intro', 'up'],
    ['.mx-footnote', 'up'],
    ['.mx-logos__label', 'left']
  ];
  var CSS_GROUPS = [
    ['.mx-logos__track',    '.mx-logos__item',      'pop', 45],
    ['.mx-carousel__track', '.mx-prod, .mx-review', 'up',  90]
  ];

  function cssReveal(scope, onlySel) {
    function settle(el, delay) {
      if (el.hasAttribute('data-mx-category-card')) {
        window.setTimeout(function () { el.style.transitionDelay = ''; }, 700 + delay);
        return;
      }
      window.setTimeout(function () {
        el.classList.remove('mx-reveal', 'mx-in', 'mx-reveal--' + el.dataset.mxAnim);
        el.style.transitionDelay = '';
      }, 1200 + delay);
    }
    function show(el, delay) {
      if (delay) el.style.transitionDelay = delay + 'ms';
      el.classList.add('mx-in');
      settle(el, delay || 0);
    }
    function prime(el, variant) {
      el.dataset.mxAnim = el.dataset.mxAnim || variant;
      el.classList.add('mx-reveal', 'mx-reveal--' + el.dataset.mxAnim);
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        obs.unobserve(el);
        if (el.dataset.mxRevealGroup != null) {
          var gap = parseInt(el.dataset.mxRevealGroup, 10) || 80;
          Array.prototype.forEach.call(el.querySelectorAll('.mx-reveal'), function (kid, n) {
            show(kid, Math.min(n * gap, STAGGER_CAP));
          });
        } else {
          show(el, parseInt(el.dataset.mxReveal, 10) || 0);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    if (!onlySel) {
      CSS_SINGLES.forEach(function (rule) {
        each(scope, rule[0], function (el) {
          if (!el.closest('.mx-home') || el.closest('.mx-head')) return;
          if (!el.hasAttribute('data-mx-reveal')) el.setAttribute('data-mx-reveal', '');
          el.dataset.mxAnim = el.dataset.mxAnim || rule[1];
        });
      });
      CSS_GROUPS.forEach(function (rule) {
        each(scope, rule[0], function (box) {
          if (!claim(box, 'RevealGroup')) return;
          box.dataset.mxRevealGroup = rule[3];
          Array.prototype.forEach.call(box.querySelectorAll(rule[1]), function (kid) { prime(kid, rule[2]); });
          observer.observe(box);
        });
      });
    }

    each(scope, onlySel || '[data-mx-reveal]', function (el) {
      if (!claim(el, 'Reveal')) return;
      prime(el, 'up');
      observer.observe(el);
    });
  }

  /* ── GSAP engine ───────────────────────────────────────────────────── */
  function gsapReveal(scope) {
    var gsap = window.gsap;
    var ST = window.ScrollTrigger;
    gsap.registerPlugin(ST);

    var START = 'top 86%';
    var CLEAR = 'transform,opacity,clipPath,filter,visibility';

    function done(el) { el.dataset.mxGs = '1'; }
    function mine(el) {
      return el && !el.dataset.mxGs && el.closest('.mx-home') && !el.closest('.mx-head') &&
             !el.hasAttribute('data-mx-category-card');
    }
    function trig(el, extra) {
      var t = { trigger: el, start: START, once: true };
      for (var k in extra) t[k] = extra[k];
      return t;
    }

    /* Headings: words rise out of their masks */
    each(scope, '.mx-h2, .mx-proof__head, .mx-rebrand__title', function (el) {
      if (!mine(el)) return;
      done(el);
      var words = splitWords(el);
      gsap.from(words, {
        yPercent: 115,
        rotate: 5,
        transformOrigin: '0% 100%',
        duration: 1.05,
        ease: 'power4.out',
        stagger: 0.055,
        scrollTrigger: trig(el),
        onComplete: function () { el.classList.add('mx-split--done'); gsap.set(words, { clearProps: 'transform' }); }
      });
    });

    /* Small lead-ins: eyebrows, intros, labels */
    each(scope, '.mx-cats__eyebrow, .mx-cats__intro, .mx-logos__label', function (el) {
      if (!mine(el)) return;
      done(el);
      gsap.from(el, {
        y: 24, opacity: 0, filter: 'blur(6px)', duration: 0.9, ease: 'power3.out',
        scrollTrigger: trig(el), clearProps: CLEAR
      });
    });

    /* Card rows: cards rise in turn; their image wipes open from the bottom
       and settles from a zoom, a toned-down take on the old homepage's
       background scale-in. */
    function cards(boxSel, kidSel, mediaSel, opts) {
      each(scope, boxSel, function (box) {
        if (!mine(box)) return;
        done(box);
        var kids = Array.prototype.filter.call(box.querySelectorAll(kidSel), mine);
        if (!kids.length) return;
        kids.forEach(done);
        var tl = gsap.timeline({ scrollTrigger: trig(box) });
        tl.from(kids, {
          y: opts.y || 70,
          opacity: 0,
          scale: opts.scale || 0.97,
          duration: 1,
          ease: 'power3.out',
          stagger: { each: opts.gap || 0.09, amount: Math.min((kids.length - 1) * (opts.gap || 0.09), STAGGER_CAP / 1000) },
          clearProps: CLEAR
        }, 0);
        if (mediaSel) {
          var media = [], imgs = [];
          kids.forEach(function (k) {
            var m = k.querySelector(mediaSel);
            if (!m) return;
            media.push(m);
            var img = m.querySelector('img');
            if (img) imgs.push(img);
          });
          if (media.length) {
            tl.from(media, {
              clipPath: 'inset(100% 0% 0% 0%)',
              duration: 1.1,
              ease: 'power4.out',
              stagger: opts.gap || 0.09,
              clearProps: 'clipPath'
            }, 0.12);
          }
          if (imgs.length) {
            tl.from(imgs, {
              scale: 1.25,
              duration: 1.5,
              ease: 'power3.out',
              stagger: opts.gap || 0.09,
              clearProps: 'transform'
            }, 0.12);
          }
        }
      });
    }
    cards('.mx-carousel__track', '.mx-prod', '.mx-prod__media', {});
    cards('.mx-carousel__track', '.mx-review', null, { y: 60, gap: 0.08 });
    cards('.mx-deco__grid', '.mx-method', 'img', { gap: 0.1 });
    cards('.mx-proof__grid', '.mx-proof__cell', null, { y: 50, gap: 0.1 });

    /* Logos: a quick wave with a little overshoot */
    each(scope, '.mx-logos__track', function (box) {
      if (!mine(box)) return;
      done(box);
      gsap.from(box.querySelectorAll('.mx-logos__item'), {
        y: 26, opacity: 0, scale: 0.85, duration: 0.7, ease: 'back.out(1.7)',
        stagger: { each: 0.04, amount: 0.6 },
        scrollTrigger: trig(box), clearProps: CLEAR
      });
    });

    /* Rebrand banner: the card grows into place, then its contents follow */
    each(scope, '.mx-rebrand__card', function (card) {
      if (!mine(card)) return;
      done(card);
      var tl = gsap.timeline({ scrollTrigger: trig(card) });
      tl.from(card, { y: 60, scale: 0.94, opacity: 0, duration: 1.1, ease: 'power3.out', clearProps: CLEAR });
      var inner = card.querySelectorAll('.mx-rebrand__inner > :not(.mx-rebrand__title)');
      if (inner.length) {
        tl.from(inner, { y: 24, opacity: 0, duration: 0.8, ease: 'power3.out', stagger: 0.1, clearProps: CLEAR }, 0.35);
      }
    });

    /* Footnotes: text rises, the button pops */
    each(scope, '.mx-footnote', function (el) {
      if (!mine(el)) return;
      done(el);
      var tl = gsap.timeline({ scrollTrigger: trig(el) });
      var text = el.querySelectorAll(':scope > :not(.mx-btn)');
      var btn = el.querySelectorAll('.mx-btn');
      if (text.length) tl.from(text, { y: 30, opacity: 0, duration: 0.9, ease: 'power3.out', clearProps: CLEAR }, 0);
      if (btn.length) tl.from(btn, { scale: 0.7, opacity: 0, duration: 0.8, ease: 'back.out(2)', clearProps: CLEAR }, 0.2);
    });

    /* Count-ups: "20,000+", "30+" run up from zero once they are seen.
       The Google rating is skipped; another script fills it in live. */
    each(scope, '.mx-logos__label, .mx-proof__years .mx-proof__stat', function (el) {
      if (el.dataset.mxCount) return;
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        var m = node.textContent.match(/\d[\d,]*/);
        if (!m) continue;
        el.dataset.mxCount = '1';
        var target = parseInt(m[0].replace(/,/g, ''), 10);
        if (!target || target < 10) break;
        var span = document.createElement('span');
        span.className = 'mx-count';
        span.textContent = m[0];
        var after = node.splitText(m.index);
        after.textContent = after.textContent.slice(m[0].length);
        node.parentNode.insertBefore(span, after);
        var width = m[0].length;
        span.style.minWidth = width + 'ch';
        var obj = { v: 0 };
        span.textContent = '0';
        gsap.to(obj, {
          v: target,
          duration: 1.8,
          ease: 'power2.out',
          scrollTrigger: trig(el),
          onUpdate: function () { span.textContent = Math.round(obj.v).toLocaleString('en-CA'); }
        });
        break;
      }
    });

    /* Everything else still tagged data-mx-reveal: a plain fade-up */
    each(scope, '[data-mx-reveal], .mx-ind__acc', function (el) {
      if (!mine(el)) return;
      done(el);
      var delay = (parseInt(el.dataset.mxReveal, 10) || 0) / 1000;
      gsap.from(el, {
        y: 50, opacity: 0, duration: 1, delay: delay, ease: 'power3.out',
        scrollTrigger: trig(el), clearProps: CLEAR
      });
    });

    /* Category cards keep the CSS engine; their styles build on .mx-in */
    cssReveal(scope, '[data-mx-category-card]');

    /* Parallax, scrubbed to the scroll: the hero drifts as it leaves, and a
       few decorative pieces float at their own pace. */
    each(scope, '.mx-hero', function (hero) {
      if (!claim(hero, 'Parallax')) return;
      var slides = hero.querySelector('.mx-hero__slides');
      if (!slides) return;
      gsap.to(slides, {
        y: 110,
        opacity: 0.55,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6 }
      });
    });
    /* Not the rebrand logo: it is centred with translateY(-50%), which a
       transform tween would overwrite. */
    each(scope, '.mx-ind__leaf, .mx-proof__map-art', function (el) {
      if (!claim(el, 'Parallax')) return;
      gsap.fromTo(el, { yPercent: 8 }, {
        yPercent: -8,
        ease: 'none',
        scrollTrigger: { trigger: el.closest('section, .shopify-section') || el, start: 'top bottom', end: 'bottom top', scrub: 0.8 }
      });
    });

    /* Late images change the page height; re-measure once they are in */
    if (!window.__mxStRefresh) {
      window.__mxStRefresh = true;
      window.addEventListener('load', function () { ST.refresh(); });
    }
  }

  function initReveal(scope) {
    if (reduceMotion) return;

    /* The hero title is split everywhere (even without GSAP): its words are
       animated in CSS each time a slide comes on. */
    each(scope, '.mx-hero__title', function (el) {
      splitWords(el);
      el.classList.add('mx-split');
    });

    if (window.gsap && window.ScrollTrigger) gsapReveal(scope);
    else if ('IntersectionObserver' in window) cssReveal(scope);
  }

  /* ======================================================================== */
  function initAll(scope) {
    initAnnouncement(scope);
    initHeader(scope);
    initHero(scope);
    initCarousels(scope);
    initAccordion(scope);
    initProcess(scope);
    initReviews(scope);
    initCategories(scope);
    initDecoration(scope);
    initSameDay(scope);
    initReveal(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme Editor: a section that is re-rendered arrives unwired */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
